import React, { useEffect, useState } from "react";

import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
} from "@mui/material";

import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import PaymentsIcon from "@mui/icons-material/Payments";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import InventoryIcon from "@mui/icons-material/Inventory";
import LocalMallIcon from "@mui/icons-material/LocalMall";
import PeopleIcon from "@mui/icons-material/People";
import StoreIcon from "@mui/icons-material/Store";
import { collection, getDocs, query, where, Timestamp } from "firebase/firestore";
import { db } from "../firebase"; // your firebase config
import { getAuth } from "firebase/auth";
import { motion } from "framer-motion";


const StatCard = ({ title, value, icon, color }) => (
  <Card
    sx={{
      borderRadius: 3,
      boxShadow: 3,
      background: `linear-gradient(135deg, ${color} 0%, #ffffff 120%)`,
    }}
  >
    <CardContent>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="body2" color="text.secondary">
            {title}
          </Typography>
          <Typography variant="h5" fontWeight="bold">
            {value}
          </Typography>
        </Box>
        <Box
          sx={{
            bgcolor: "rgba(255,255,255,0.7)",
            p: 1.5,
            borderRadius: "50%",
          }}
        >
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

export default function Dashboard() {
  // 🔥 Dummy values (replace with Firestore data later)
  const [paymentCards, setPaymentCards] = useState([]);

  const [dashboardData, setDashboardData] = useState({
    todaysOrders: 0,
    todaysCollection: 0,
    monthlyTotal: 0,
    totalProducts: 0,
    totalPurchase: 0,
    customerOutstanding: 0,
    vendorOutstanding: 0,
  });

  const startOfToday = Timestamp.fromDate(
    new Date(new Date().setHours(0, 0, 0, 0))
  );

  const startOfMonth = Timestamp.fromDate(
    new Date(new Date().getFullYear(), new Date().getMonth(), 1)
  );

  const auth = getAuth();
  const user = auth.currentUser;

  const getPropertyId = async () => {
    const roleSnap = await getDocs(
      query(
        collection(db, "authroles"),
        where("uid", "==", user.uid)
      )
    );

    if (!roleSnap.empty) {
      return roleSnap.docs[0].data().propertyId;
    }

    return null;
  };


  useEffect(() => {
    if (!user) return;

    const loadDashboard = async () => {

      const propertyId = await getPropertyId();
      if (!propertyId) return;

      /* 🔹 Orders */
      const ordersSnap = await getDocs(
        query(
          collection(db, "orders"),
          where("propertyId", "==", propertyId)
        )
      );

      let todaysOrders = 0;
      let todaysCollection = 0;
      let monthlyTotal = 0;

      ordersSnap.forEach(doc => {
        const data = doc.data();

        if (data.createdAt >= startOfToday) {
          todaysOrders += 1;
          todaysCollection += data.totalamount || 0;

        }

        if (data.createdAt >= startOfMonth) {
          monthlyTotal += data.totalamount || 0;
        }
      });

      /* 🔹 Payment Modes Master */
      const paymentModeSnap = await getDocs(
        query(
          collection(db, "restopaymodes"),
          where("propertyId", "==", propertyId)
        )
      );

      // paymentModeId -> name
      const paymentModeMap = {};
      paymentModeSnap.forEach(doc => {
        paymentModeMap[doc.id] = doc.data().paymode;
      });

      // initialize totals
      const paymentTotals = {};
      Object.keys(paymentModeMap).forEach(id => {
        paymentTotals[id] = 0;
      });

      /* 🔹 Orders (already fetched) */
      ordersSnap.forEach(doc => {
        const data = doc.data();

        if (data.paymentmode && paymentTotals[data.paymentmode] !== undefined) {
          paymentTotals[data.paymentmode] += data.totalamount || 0;
        }
      });

      /* 🔹 Build cards dynamically */
      const dynamicPaymentCards = Object.keys(paymentTotals).map(id => ({
        id,
        title: paymentModeMap[id],
        amount: paymentTotals[id],
      }));

      setPaymentCards(dynamicPaymentCards);


      /* 🔹 Products */
      const productsSnap = await getDocs(
        query(
          collection(db, "products"),
          where("propertyId", "==", propertyId)
        )
      );

      /* 🔹 Purchases */
      const purchaseSnap = await getDocs(
        query(
          collection(db, "purchases"),
          where("propertyId", "==", propertyId)
        )
      );

      let totalPurchase = 0;
      purchaseSnap.forEach(doc => {
        totalPurchase += doc.data().amount || 0;
      });

      /* 🔹 Customer Outstanding */
      const customerSnap = await getDocs(
        query(
          collection(db, "restocustomers"),
          where("propertyId", "==", propertyId)
        )
      );

      let customerOutstanding = 0;
      customerSnap.forEach(doc => {
        customerOutstanding += doc.data().outstandingAmount || 0;
      });

      /* 🔹 Vendor Outstanding */
      const vendorSnap = await getDocs(
        query(
          collection(db, "dealers"),
          where("propertyId", "==", propertyId)
        )
      );

      let vendorOutstanding = 0;
      vendorSnap.forEach(doc => {
        vendorOutstanding += doc.data().outstandingAmount || 0;
      });

      setDashboardData({
        todaysOrders,
        todaysCollection,
        monthlyTotal,
        totalProducts: productsSnap.size,
        totalPurchase,
        customerOutstanding,
        vendorOutstanding,
      });
    };



    loadDashboard();
  }, [user]);

  return (
    <Box className=" p-3">
      <Card>
        <CardContent>
          <Box mb={2}>
            <Typography variant="h5" fontWeight="bold">
              Dashboard
            </Typography>
          </Box>

          {/* Main Stats */}
          <Grid className="row" spacing={3}>
            {[
              {
                title: "Today's Orders",
                value: dashboardData.todaysOrders,
                icon: <ShoppingCartIcon />,
                color: "#4F46E5",
              },
              {
                title: "Today's Collection",
                value: `₹ ${dashboardData.todaysCollection}`,
                icon: <CurrencyRupeeIcon />,
                color: "#16A34A",
              },
              {
                title: "Monthly Revenue",
                value: `₹ ${dashboardData.monthlyTotal}`,
                icon: <PaymentsIcon />,
                color: "#9333EA",
              },
              {
                title: "Total Products",
                value: dashboardData.totalProducts,
                icon: <InventoryIcon />,
                color: "#0EA5E9",
              },
              {
                title: "Total Purchase",
                value: `₹ ${dashboardData.totalPurchase}`,
                icon: <LocalMallIcon />,
                color: "#EF4444",
              },
              {
                title: "Customer Outstanding",
                value: `₹ ${dashboardData.customerOutstanding}`,
                icon: <PeopleIcon />,
                color: "#DC2626",
              },
              {
                title: "Vendor Outstanding",
                value: `₹ ${dashboardData.vendorOutstanding}`,
                icon: <StoreIcon />,
                color: "#F59E0B",
              },

            ].map((item, index) => (
              <Grid className="col-sm-3 mb-3" item xs={12} sm={6} md={3} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 40 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card
                    sx={{
                      borderRadius: 4,
                      p: 2,
                      backdropFilter: "blur(10px)",
                      background: "rgba(255,255,255,0.8)",
                      boxShadow: "0 15px 40px rgba(0,0,0,0.1)",
                      transition: "0.3s",
                      "&:hover": {
                        transform: "translateY(-5px)",
                        boxShadow: "0 15px 40px rgba(0,0,0,0.1)",
                      },
                    }}
                  >
                    <CardContent>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{ color: "text.secondary" }}
                          >
                            {item.title}
                          </Typography>

                          <Typography
                            variant="h5"
                            fontWeight="bold"
                            mt={1}
                          >
                            {item.value}
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            height: 50,
                            width: 50,
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: item.color,
                            color: "#fff",
                          }}
                        >
                          {item.icon}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
            <Grid className="col-sm-3 mb-3" item xs={12} sm={6} md={3}> </Grid>

            {/* Dynamic Payment Modes */}
            {paymentCards.map((pm, index) => (
              <Grid className="col-sm-3 mb-3" item xs={12} sm={6} md={3} key={pm.id}>
                <motion.div
                  initial={{ opacity: 0, y: 40 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card
                    sx={{
                      borderRadius: 4,
                      p: 2,
                      background: "white",
                      boxShadow: "0 8px 30px rgba(0,0,0,0.05)",
                      transition: "0.3s",
                      "&:hover": {
                        transform: "translateY(-5px)",
                        boxShadow: "0 15px 40px rgba(0,0,0,0.1)",
                      },
                    }}
                  >
                    <CardContent>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Box>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                          >
                            {pm.title}
                          </Typography>

                          <Typography variant="h5" fontWeight="bold" mt={1}>
                            ₹ {pm.amount}
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            height: 50,
                            width: 50,
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: "#0284C7",
                            color: "#fff",
                          }}
                        >
                          <AccountBalanceWalletIcon />
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
}
